import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import {
  Trash2,
  Plus,
  Loader2,
  AlertCircle,
  Database,
  FileText,
  FileSpreadsheet,
  FileJson,
  Upload,
  Eye,
  Download,
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import {
  StorageConfig,
  DataFile,
  DataFilePreview,
  getStorageConfig,
  updateStorageConfig,
  listDataFiles,
  uploadDataFile,
  previewDataFile,
  deleteDataFile,
  formatBytes,
} from '@/services/storageApi';

interface StorageTabProps {
  className?: string;
}

export function StorageTab({ className }: StorageTabProps) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Storage config state
  const [config, setConfig] = useState<StorageConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  // Data files state
  const [files, setFiles] = useState<DataFile[]>([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);

  // Upload dialog state
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  // Preview dialog state
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewData, setPreviewData] = useState<DataFilePreview | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  // Delete confirmation state
  const [deleteFile, setDeleteFile] = useState<DataFile | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await getStorageConfig();
      setConfig(data);
    } catch (error) {
      console.error('Failed to fetch storage config:', error);
      toast({
        title: 'Error',
        description: 'Failed to load storage configuration',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  const fetchFiles = useCallback(async () => {
    if (!config?.is_enabled) return;

    try {
      setIsLoadingFiles(true);
      const data = await listDataFiles(false);
      setFiles(data);
    } catch (error) {
      console.error('Failed to fetch data files:', error);
      toast({
        title: 'Error',
        description: 'Failed to load data files',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingFiles(false);
    }
  }, [config?.is_enabled, toast]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  useEffect(() => {
    if (config?.is_enabled) {
      fetchFiles();
    }
  }, [config?.is_enabled, fetchFiles]);

  const handleToggleEnabled = async (enabled: boolean) => {
    try {
      setIsUpdating(true);
      const updated = await updateStorageConfig({ is_enabled: enabled });
      setConfig(updated);
      toast({
        title: enabled ? 'Storage Enabled' : 'Storage Disabled',
        description: enabled
          ? 'You can now upload data files for eval functions.'
          : 'Storage has been disabled for this organization.',
      });
      if (enabled) {
        fetchFiles();
      }
    } catch (error: unknown) {
      console.error('Failed to update storage config:', error);
      const message = error instanceof Error ? error.message : 'Failed to update storage configuration';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (1MB limit)
      if (file.size > 1024 * 1024) {
        toast({
          title: 'File Too Large',
          description: 'Maximum file size is 1MB.',
          variant: 'destructive',
        });
        return;
      }

      setUploadFile(file);
      // Use filename without extension as default name
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
      setUploadName(nameWithoutExt);
      setShowUploadDialog(true);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadName.trim()) return;

    try {
      setIsUploading(true);
      const result = await uploadDataFile(uploadFile, uploadName.trim(), uploadDescription.trim() || undefined);
      toast({
        title: 'File Uploaded',
        description: result.message,
      });
      setShowUploadDialog(false);
      setUploadFile(null);
      setUploadName('');
      setUploadDescription('');
      fetchFiles();
      fetchConfig(); // Refresh quota
    } catch (error: unknown) {
      console.error('Failed to upload file:', error);
      const message = error instanceof Error ? error.message : 'Failed to upload file';
      toast({
        title: 'Upload Failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handlePreview = async (file: DataFile) => {
    try {
      setIsLoadingPreview(true);
      setShowPreviewDialog(true);
      const data = await previewDataFile(file.id, 20);
      setPreviewData(data);
    } catch (error) {
      console.error('Failed to preview file:', error);
      toast({
        title: 'Error',
        description: 'Failed to load file preview',
        variant: 'destructive',
      });
      setShowPreviewDialog(false);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteFile) return;

    try {
      setIsDeleting(true);
      const result = await deleteDataFile(deleteFile.id, true);
      toast({
        title: 'File Deleted',
        description: result.message || `File "${deleteFile.name}" has been deleted.`,
      });
      setDeleteFile(null);
      fetchFiles();
      fetchConfig(); // Refresh quota
    } catch (error: unknown) {
      console.error('Failed to delete file:', error);
      const message = error instanceof Error ? error.message : 'Failed to delete file';
      toast({
        title: 'Delete Failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const getFileIcon = (fileType: string) => {
    switch (fileType) {
      case 'csv':
        return <FileSpreadsheet className="h-4 w-4" />;
      case 'json':
        return <FileJson className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Storage Configuration Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Data Storage
              </CardTitle>
              <CardDescription>
                Upload static data files (CSV, JSON, TXT) for use in eval functions.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="storage-toggle" className="text-sm">
                {config?.is_enabled ? 'Enabled' : 'Disabled'}
              </Label>
              <Switch
                id="storage-toggle"
                checked={config?.is_enabled || false}
                onCheckedChange={handleToggleEnabled}
                disabled={isUpdating}
              />
            </div>
          </div>
        </CardHeader>

        {config?.is_enabled && (
          <CardContent>
            {/* Quota Progress */}
            <div className="space-y-4 mb-6">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Storage Used</span>
                  <span className="text-muted-foreground">
                    {formatBytes(config.current_size_bytes)} / {config.max_total_size_mb} MB
                  </span>
                </div>
                <Progress value={config.usage_percentage} className="h-2" />
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Files</span>
                  <span className="text-muted-foreground">
                    {config.current_file_count} / {config.max_file_count}
                  </span>
                </div>
                <Progress value={config.file_count_percentage} className="h-2" />
              </div>
            </div>

            {/* Upload Button */}
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Data Files</h3>
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept=".csv,.json,.txt"
                  className="hidden"
                />
                <Button onClick={() => fileInputRef.current?.click()}>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload File
                </Button>
              </div>
            </div>

            {/* Files Table */}
            {isLoadingFiles ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : files.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No data files uploaded yet.</p>
                <p className="text-sm">Upload CSV, JSON, or TXT files for your eval functions.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Uses</TableHead>
                    <TableHead>Uploaded</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {files.map((file) => (
                    <TableRow key={file.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getFileIcon(file.file_type)}
                          <div>
                            <div className="font-medium">{file.name}</div>
                            {file.description && (
                              <div className="text-sm text-muted-foreground truncate max-w-xs">
                                {file.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{file.file_type.toUpperCase()}</Badge>
                      </TableCell>
                      <TableCell>{formatBytes(file.file_size)}</TableCell>
                      <TableCell>{file.usage_count}</TableCell>
                      <TableCell>{formatDate(file.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePreview(file)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteFile(file)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        )}

        {!config?.is_enabled && (
          <CardContent>
            <div className="text-center py-8 text-muted-foreground">
              <AlertCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>Storage is disabled for this organization.</p>
              <p className="text-sm">Enable storage to upload data files for eval functions.</p>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Data File</DialogTitle>
            <DialogDescription>
              Upload a CSV, JSON, or TXT file for use in eval functions.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {uploadFile && (
              <div className="p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-2">
                  {getFileIcon(uploadFile.name.endsWith('.csv') ? 'csv' : uploadFile.name.endsWith('.json') ? 'json' : 'text')}
                  <span className="font-medium">{uploadFile.name}</span>
                  <Badge variant="outline">{formatBytes(uploadFile.size)}</Badge>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="file-name">Name *</Label>
              <Input
                id="file-name"
                value={uploadName}
                onChange={(e) => setUploadName(e.target.value)}
                placeholder="Enter a unique name for this file"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="file-description">Description</Label>
              <Textarea
                id="file-description"
                value={uploadDescription}
                onChange={(e) => setUploadDescription(e.target.value)}
                placeholder="Optional description"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={!uploadName.trim() || isUploading}>
              {isUploading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>File Preview</DialogTitle>
            {previewData && (
              <DialogDescription>
                {previewData.name} ({previewData.file_type.toUpperCase()})
                {previewData.truncated && ' - Showing first 20 lines'}
              </DialogDescription>
            )}
          </DialogHeader>

          <div className="py-4">
            {isLoadingPreview ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : previewData ? (
              <pre className="p-4 bg-muted rounded-lg overflow-auto max-h-96 text-sm font-mono whitespace-pre-wrap">
                {previewData.preview}
              </pre>
            ) : null}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteFile} onOpenChange={() => setDeleteFile(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete File</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteFile?.name}"? This action cannot be undone.
              {deleteFile && (deleteFile.usage_count ?? 0) > 0 && (
                <span className="block mt-2 text-yellow-600">
                  Warning: This file is used by {deleteFile.usage_count} eval function(s).
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteFile(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default StorageTab;
